import React, { MouseEvent, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import isEmail from "is-email";
import Link from "next/link";
import { useRouter } from "next/router";
import { useStoreState } from "pullstate";
import classNames from "classnames";
import Image from "next/image";
import * as Sentry from "@sentry/nextjs";

import { apiPath } from "shared/helpers/apiPath";
import EmailIcon from "@/icons/email.svg";
import { routePath } from "@/helpers/routePath";
import client from "@/helpers/apiClient";
import Typography from "@/components/Typography";
import Input from "@/components/Input";
import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import SocialLogin from "@/components/SocialLogin";
import { AuthFormState } from "@/helpers/pageHelpers/Store/interface";
import { applicationRoutes } from "@/helpers/applicationRouting";
import { UIStore } from "@/store/UIStore";
import Container from "@/components/Container";
import Label from "@/components/Label";
import { CheckboxVersion } from "@/components/Checkbox/Checkbox.interface";
import { getImgixURL } from "@/helpers/getImgixURL";
import CheckedIcon from "@/icons/small-checkmark.svg";

import styles from "./RegisterForm.module.scss";
import { FormInterface, RegisterFormInterface } from "./RegisterForm.interface";

export const RegisterForm: React.FC<RegisterFormInterface> = ({
  changeFormState,
  onClose,
}) => {
  const [isEmailFormAvailable, setEmailFormVisibility] =
    useState<boolean>(false);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors },
  } = useForm<FormInterface>();
  const router = useRouter();
  const { authFormState } = useStoreState(UIStore, (store) => store);
  const value = useWatch({ name: "password", control });
  const passwordContainsNumber = /[0-9]/.test(value);
  const passwordContainsUpperAndLowercaselLetters =
    /[A-Z]/.test(value) && /[a-z]/.test(value);
  const passwordHasSixCharacters = value?.length >= 6;
  const isPasswordCorrect =
    passwordHasSixCharacters &&
    passwordContainsUpperAndLowercaselLetters &&
    passwordContainsNumber;

  const onSubmit = async (data: FormInterface) => {
    setSubmitting(true);
    const redirectUrl =
      process.env.NEXT_PUBLIC_DOMAIN + routePath.ONBOARDING.href;

    const userData = {
      redirectUrl,
      ...data,
    };

    try {
      await client.post(apiPath.USERS, userData);
      window?.fbq("track", "CompleteRegistration");
      UIStore.update((store) => {
        store.authFormState = AuthFormState.ThankYou;
      });
    } catch ({ response }) {
      Sentry.captureMessage(`user ma problem z rejestracją`);
      const errors = response.data.errors;
      if (errors?.displayName) {
        setError("displayName", {
          type: "manual",
          message: "Podana nazwa użytkownika jest już zajęta",
        });
      } else if (errors?.email) {
        setError("email", {
          type: "manual",
          message:
            "Konto założone na taki email już istnieje - możesz logować się tymi samymi danymi zarówno na newonce.net jak i newonce.sport. Jedno konto. Cały świat newonce.",
        });
      } else if (errors?.password) {
        setError("passwordConfirmation", {
          type: "manual",
          message: "To hasło musi być takie samo jak to wyżej!",
        });
      } else {
        setError("email", {
          type: "manual",
          message:
            "Coś poszło nie tak, skontaktuj się z nami pod inbox@newonce.net",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginLinkClick = (event: MouseEvent<HTMLElement>) => {
    if (changeFormState) {
      event.preventDefault();
      changeFormState(AuthFormState.Login);
    } else {
      event.stopPropagation();
    }
  };

  const handleGoToFeed = () => {
    router.push(routePath.FEED.href);
    onClose && onClose();
  };

  const getThankYouMessage = () => {
    return "Na Twój adres e-mail wysłaliśmy link aktywacyjny. Po jego kliknięciu będziesz w stanie spersonalizować feed, zapisywać treści na później i obserwować podcasty oraz redaktorów. Mówiąc krótko - będziesz w newonce.";
  };

  return authFormState !== AuthFormState.ThankYou ? (
    <div className={styles.container}>
      <Typography variant="h5" component="h3" className={styles.heading}>
        Załóż konto za pomocą
      </Typography>

      <Container>
        <>
          <SocialLogin
            onClose={onClose}
            setError={setError}
            isFluid={!isEmailFormAvailable}
          />

          <Typography
            variant="medium"
            component="p"
            className={styles.subheading}
          >
            <span className={styles.label}>lub</span>
          </Typography>
        </>

        {!isEmailFormAvailable ? (
          <Button
            className={styles.showForm}
            variant="secondary"
            onClick={() => setEmailFormVisibility(true)}
          >
            <EmailIcon />
            <span className={styles.email}>Kontynuuj przez e-mail</span>
          </Button>
        ) : (
          <form
            className={styles.form}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <Label label="Adres email" input="email" />
            <Input
              {...register("email", {
                required: "Email jest wymagany",
                validate: (input) => {
                  const isemail = isEmail(input);
                  if (!isemail) {
                    return "Podany email jest w złej formie :(";
                  }
                },
              })}
              type="email"
              placeholder="Twój adres email"
              className={styles.input}
              error={errors.email?.message}
            />

            <Label label="Hasło" input="password" />
            <Input
              {...register("password", {
                required: "Hasło jest wymagane",
                validate: () => {
                  if (!isPasswordCorrect) {
                    return "Twoje hasło musi spełniać wszystkie warunki poniżej";
                  }
                },
              })}
              type="password"
              placeholder="Hasło"
              className={styles.input}
              error={errors.password?.message}
            />
            <div className={styles.passwordValidation}>
              <Typography variant="small" component="p">
                Hasło musi zawierać:
              </Typography>
              <Typography
                variant="small"
                component="p"
                className={classNames(
                  styles.listElement,
                  passwordHasSixCharacters && styles.active
                )}
              >
                <CheckedIcon />
                Co najmniej 6 znaków
              </Typography>
              <Typography
                variant="small"
                component="p"
                className={classNames(
                  styles.listElement,
                  passwordContainsUpperAndLowercaselLetters && styles.active
                )}
              >
                <CheckedIcon />
                Wielkie i małe litery
              </Typography>
              <Typography
                variant="small"
                component="p"
                className={classNames(
                  styles.listElement,
                  passwordContainsNumber && styles.active
                )}
              >
                <CheckedIcon />
                Co najmniej jedną cyfrę
              </Typography>
            </div>

            <Label label="Powtórz hasło" input="passwordConfirmation" />
            <Input
              {...register("passwordConfirmation", {
                required:
                  "To pole też jest wymagane. I musi być takie samo jak to wyżej.",
                validate: () => {
                  if (!isPasswordCorrect) {
                    return "To pole musi być takie samo jak to wyżej";
                  }
                },
              })}
              type="password"
              placeholder="Powtórz Hasło"
              className={styles.input}
              error={errors.passwordConfirmation?.message}
            />

            <div className={styles.terms}>
              <Checkbox
                {...register("newonceTermsAccepted", {
                  required: "Pole jest wymagane",
                })}
                error={!!errors.newonceTermsAccepted}
                className={styles.checkbox}
                version={CheckboxVersion.second}
              />

              <Typography variant="small" component="p" className={styles.note}>
                Rejestrując się, wyrażasz zgodę na
                <Link
                  href={routePath.PAGE.href}
                  as={applicationRoutes.FEED + "regulamin"}
                >
                  <a className={styles.link}>warunki korzystania</a>
                </Link>
                z usługi i
                <Link
                  href={routePath.PAGE.href}
                  as={applicationRoutes.FEED + "polityka-prywatnosci"}
                >
                  <a className={styles.link}>politykę prywatności.</a>
                </Link>
              </Typography>
            </div>
            <Button
              type="submit"
              variant="primary"
              className={classNames(styles.button, "register_email")}
              isLoading={isSubmitting}
            >
              Zarejestruj się!
            </Button>
          </form>
        )}

        <Typography variant="medium" component="p" className={styles.loginLink}>
          Masz już konto w newonce?
          <Link href={routePath.LOGIN.href}>
            <a className={styles.link} onClick={handleLoginLinkClick}>
              Zaloguj się
            </a>
          </Link>
        </Typography>
      </Container>
    </div>
  ) : (
    <div className={classNames(styles.container, styles.confirmation)}>
      <Container>
        <Image
          src={getImgixURL(
            "https://cdn.newonce.me/uploads/images/45480/Goodies_Heart.png"
          )}
          height={100}
          width={170}
          alt="thank you"
        />
        <Typography
          variant="h5"
          component="h5"
          className={classNames(styles.heading, styles.confirmation)}
        >
          Dziękujemy za rejestrację!
        </Typography>

        <Typography variant="medium" component="p" className={styles.message}>
          {getThankYouMessage()}
        </Typography>

        <Button
          variant="primary"
          className={classNames(styles.button, styles.confirmation)}
          onClick={handleGoToFeed}
        >
          Przejdź do newonce
        </Button>
      </Container>
    </div>
  );
};

export default RegisterForm;
